import React from 'react';
import { createPortal } from 'react-dom';
import { DETAILS_COLUMN_BY_ID } from '../detailsColumns';
import { OrderStatusTypes, statusBadgeClassName } from '../../../types/OrderStatusTypes';
import { PaymentStatusTypes } from '../../../types/PaymentStatusTypes';
import { isExpenseOrder, isInvestmentOrder, isReplenishOrder, isSalesOrder } from '../../../types/OrderTypes';
import CancelOrderConfirmModal from './CancelOrderConfirmModal';
import './OrdersBoard.css';

const PAGE_SIZE = 20;
const EDGE_PX = 64;
const MAX_SCROLL_SPEED = 16;
const PRODUCT_COLUMN_IDS = new Set(['product', 'quantity', 'price']);

function emptyColumn(loading) {
  return {
    orders: [],
    page: 1,
    total: null,
    totalPages: 1,
    loading,
    loadingMore: false,
    error: null,
  };
}

function buildCardSections(columnOrder) {
  const sections = [];
  let products = null;
  (columnOrder || []).forEach((id) => {
    if (id === 'status' || id === 'order') return;
    if (PRODUCT_COLUMN_IDS.has(id)) {
      if (!products) {
        products = { type: 'products', fields: [] };
        sections.push(products);
      }
      products.fields.push(id);
      return;
    }
    if (DETAILS_COLUMN_BY_ID[id]) {
      sections.push({ type: 'field', id });
    }
  });
  return sections;
}

function formatMoney(value) {
  const n = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(n)) return '0.00 сом';
  return `${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} сом`;
}

function formatQuantity(value) {
  const n = typeof value === 'number' ? value : Number.parseFloat(value);
  if (!Number.isFinite(n)) return '0';
  return String(n);
}

function displayCustomerName(order) {
  if (isReplenishOrder(order) || isExpenseOrder(order) || isInvestmentOrder(order)) return '—';
  const name = String(order?.customerName ?? '').trim();
  return name || '—';
}

function displayCustomerPhone(order) {
  if (isReplenishOrder(order) || isExpenseOrder(order) || isInvestmentOrder(order)) return '—';
  const phone = String(order?.customerPhone ?? '').trim();
  if (!phone || phone === '-') return '—';
  return phone;
}

function displayComment(order) {
  const text = String(order?.customerDescription ?? '').trim();
  if (!text || text === '-') return '—';
  return text;
}

function fieldValue(columnId, order) {
  const paymentStatusId = Number(order?.paymentStatusId ?? PaymentStatusTypes.NOT_PAID);
  const isSystem = isReplenishOrder(order) || isExpenseOrder(order) || isInvestmentOrder(order);
  if (columnId === 'customer') return displayCustomerName(order);
  if (columnId === 'phone') return displayCustomerPhone(order);
  if (columnId === 'comment') return displayComment(order);
  if (columnId === 'payment') {
    if (isSystem) return '—';
    return PaymentStatusTypes.labels[paymentStatusId] ?? `Оплата ${paymentStatusId}`;
  }
  return '—';
}

function captureCardRects(root) {
  const rects = new Map();
  if (!root) return rects;
  root.querySelectorAll('[data-order-id]').forEach((node) => {
    rects.set(node.getAttribute('data-order-id'), node.getBoundingClientRect());
  });
  return rects;
}

function animateCardMoves(root, pending) {
  const before = pending?.rects;
  if (!root || !before?.size) return;
  const movedId = pending.movedId == null ? null : String(pending.movedId);
  const measured = [...root.querySelectorAll('[data-order-id]')].map((node) => {
    const id = node.getAttribute('data-order-id');
    return { node, id, prev: before.get(id), next: node.getBoundingClientRect() };
  });

  measured.forEach(({ node, id, prev, next }) => {
    if (!prev) return;
    const dx = prev.left - next.left;
    const dy = prev.top - next.top;
    if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) return;

    if (id === movedId) {
      const fly = node.cloneNode(true);
      fly.removeAttribute('data-order-id');
      fly.classList.remove('orders-board-card--source');
      fly.style.position = 'fixed';
      fly.style.left = `${prev.left}px`;
      fly.style.top = `${prev.top}px`;
      fly.style.width = `${prev.width}px`;
      fly.style.height = `${prev.height}px`;
      fly.style.margin = '0';
      fly.style.boxSizing = 'border-box';
      fly.style.zIndex = '40';
      fly.style.pointerEvents = 'none';
      fly.style.transition = 'none';
      fly.style.transform = 'none';
      document.body.appendChild(fly);
      node.style.visibility = 'hidden';
      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        fly.remove();
        node.style.visibility = '';
      };
      window.requestAnimationFrame(() => {
        fly.style.transition = 'left 240ms ease, top 240ms ease, width 240ms ease, height 240ms ease';
        fly.style.left = `${next.left}px`;
        fly.style.top = `${next.top}px`;
        fly.style.width = `${next.width}px`;
        fly.style.height = `${next.height}px`;
        fly.addEventListener('transitionend', (event) => {
          if (event.propertyName === 'top') finish();
        });
        window.setTimeout(finish, 320);
      });
      return;
    }

    node.style.transition = 'none';
    node.style.transform = `translate(${dx}px, ${dy}px)`;
    window.requestAnimationFrame(() => {
      node.style.transition = 'transform 240ms ease';
      node.style.transform = '';
      const clear = () => {
        node.style.transition = '';
        node.removeEventListener('transitionend', clear);
      };
      node.addEventListener('transitionend', clear);
    });
  });
}

function columnTotal(column) {
  return Number.isFinite(column?.total) ? column.total : column?.orders?.length ?? 0;
}

function moveBetween(columns, order, fromId, toId) {
  const from = columns[fromId];
  const to = columns[toId];
  if (!from || !to) return columns;
  return {
    ...columns,
    [fromId]: {
      ...from,
      orders: from.orders.filter((item) => item.id !== order.id),
      total: Math.max(0, columnTotal(from) - 1),
    },
    [toId]: {
      ...to,
      orders: [order, ...to.orders.filter((item) => item.id !== order.id)],
      total: columnTotal(to) + 1,
    },
  };
}

function boardFilterParams(filters) {
  const params = new URLSearchParams({
    limit: String(PAGE_SIZE),
    typeIds: String(filters.typeId),
  });
  if (filters.dateFrom) {
    params.set('dateFrom', filters.dateFrom);
    params.set('dateTo', filters.dateTo || filters.dateFrom);
  }
  if (filters.searchQuery) params.set('search', filters.searchQuery);
  return params;
}

async function fetchBoardColumns(requestWithMeta, branchId, statusIds, filters) {
  const params = boardFilterParams(filters);
  params.set('page', '1');
  params.set('statusIds', statusIds.join(','));
  const res = await requestWithMeta(
    `/operator/branches/${encodeURIComponent(branchId)}/orders/board?${params.toString()}`,
    'GET'
  );
  if (!res?.ok) {
    return {
      ok: false,
      columns: {},
      error: res?.data?.error || 'Не удалось загрузить заказы',
    };
  }
  return {
    ok: true,
    columns: res?.data?.columns && typeof res.data.columns === 'object' ? res.data.columns : {},
    error: null,
  };
}

async function fetchBoardPage(requestWithMeta, branchId, statusId, page, filters) {
  const params = boardFilterParams(filters);
  params.set('page', String(page));
  params.set('statusId', String(statusId));
  const res = await requestWithMeta(
    `/operator/branches/${encodeURIComponent(branchId)}/orders/board?${params.toString()}`,
    'GET'
  );
  if (!res?.ok) {
    return {
      ok: false,
      orders: [],
      totalPages: 1,
      error: res?.data?.error || 'Не удалось загрузить заказы',
    };
  }
  return {
    ok: true,
    orders: Array.isArray(res?.data?.orders) ? res.data.orders : [],
    total: res?.data?.pagination?.total ?? null,
    totalPages: res?.data?.pagination?.totalPages ?? 1,
    error: null,
  };
}

function ProductLines({ products, fields }) {
  if (!fields.length || !products?.length) return null;
  return (
    <ul className="orders-board-products">
      {products.map((product) => {
        const meta = [];
        if (fields.includes('quantity')) meta.push(`х${formatQuantity(product.quantity)}`);
        if (fields.includes('price')) meta.push(formatMoney(product.linePrice));
        return (
          <li key={product.id} className="orders-board-product">
            {fields.includes('product') ? (
              <span className="orders-board-productName">{product.productName || '—'}</span>
            ) : <span />}
            {meta.length ? <span className="orders-board-productMeta">{meta.join(' · ')}</span> : null}
          </li>
        );
      })}
    </ul>
  );
}

function OrderCard({
  order,
  sections,
  isSource,
  showHandle,
  onOpen,
  onHandlePointerDown,
  className = '',
  style,
}) {
  return (
    <article
      className={`orders-board-card${isSource ? ' orders-board-card--source' : ''}${className ? ` ${className}` : ''}`}
      style={style}
      data-order-id={order.id}
      onClick={onOpen}
    >
      <div className="orders-board-cardHead">
        <span className="orders-board-cardId">№ {order.id}</span>
        {showHandle ? (
          <button
            type="button"
            className="orders-board-handle"
            aria-label="Перетащить заказ"
            title="Перетащить заказ"
            onPointerDown={onHandlePointerDown}
            onClick={(event) => event.stopPropagation()}
          >
            <svg className="orders-board-handleIcon" viewBox="0 0 24 24" aria-hidden="true">
              <path
                fill="currentColor"
                d="M16 9V4h1c.55 0 1-.45 1-1s-.45-1-1-1H7c-.55 0-1 .45-1 1s.45 1 1 1h1v5c0 1.66-1.34 3-3 3v2h5.97v7l1 1 1-1v-7H19v-2c-1.66 0-3-1.34-3-3z"
              />
            </svg>
          </button>
        ) : null}
      </div>
      {sections.some((section) => section.type === 'field') ? (
        <div className="orders-board-fields">
          {sections.map((section) => {
            if (section.type !== 'field') return null;
            const column = DETAILS_COLUMN_BY_ID[section.id];
            const paymentStatusId = Number(order?.paymentStatusId ?? PaymentStatusTypes.NOT_PAID);
            const value = fieldValue(section.id, order);
            return (
              <div key={section.id} className="orders-board-field">
                {section.id === 'payment' && value !== '—' ? (
                  <span className="orders-board-paymentLine">
                    <span className="orders-board-fieldLabel">Оплата:</span>
                    <span className={`orders-page-itemPayment orders-page-itemPayment--${paymentStatusId}`}>
                      {value}
                    </span>
                  </span>
                ) : (
                  <>
                    <span className="orders-board-fieldLabel">{column.label}</span>
                    <span className="orders-board-fieldValue">{value}</span>
                  </>
                )}
              </div>
            );
          })}
        </div>
      ) : null}
      {sections.map((section) => (
        section.type === 'products' ? (
          <ProductLines key="products" products={order.products} fields={section.fields} />
        ) : null
      ))}
    </article>
  );
}

function BoardColumn({
  status,
  column,
  sections,
  isDrop,
  draggingOrderId,
  onOpenOrder,
  onOpenColumns,
  onHandlePointerDown,
  onLoadMore,
}) {
  const listRef = React.useRef(null);
  const sentinelRef = React.useRef(null);
  const headerPointer = React.useRef(null);

  React.useEffect(() => {
    const root = listRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel) return undefined;
    if (column.loading || column.loadingMore || column.page >= column.totalPages) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) onLoadMore(status.id);
      },
      { root, rootMargin: '80px', threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [column.loading, column.loadingMore, column.page, column.totalPages, onLoadMore, status.id]);

  const openColumns = (event) => {
    const start = headerPointer.current;
    headerPointer.current = null;
    if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > 10) return;
    onOpenColumns();
  };

  return (
    <section
      className={`${statusBadgeClassName('orders-board-column', status.id)}${isDrop ? ' orders-board-column--drop' : ''}`}
      data-status-id={status.id}
    >
      <button
        type="button"
        className="orders-board-columnHead"
        onPointerDown={(event) => {
          headerPointer.current = { x: event.clientX, y: event.clientY };
        }}
        onClick={openColumns}
        title="Настроить порядок колонок"
      >
        <span className={statusBadgeClassName('orders-board-columnTitle', status.id)}>
          <span className="orders-board-columnDot" aria-hidden="true" />
          <span className="orders-board-columnLabel">{status.label}</span>
        </span>
        {column.total == null ? null : (
          <span className="orders-board-columnCount">{column.total}</span>
        )}
      </button>
      <div className="orders-board-columnList" ref={listRef}>
        {column.loading ? (
          <>
            <div className="orders-board-skeleton" />
            <div className="orders-board-skeleton" />
          </>
        ) : column.error ? (
          <div className="orders-board-columnError">{column.error}</div>
        ) : column.orders.length === 0 ? (
          <div className="orders-board-empty">Нет заказов</div>
        ) : (
          column.orders.map((order) => (
            <OrderCard
              key={order.id}
              order={order}
              sections={sections}
              isSource={draggingOrderId === order.id}
              showHandle={isSalesOrder(order)}
              onOpen={() => onOpenOrder(order.id)}
              onHandlePointerDown={(event) => onHandlePointerDown(event, order, status.id)}
            />
          ))
        )}
        {column.loadingMore ? (
          <div className="orders-board-loader" role="status" aria-label="Загрузка">
            <span className="orders-board-loaderSpin" />
          </div>
        ) : null}
        <div ref={sentinelRef} className="orders-page-sentinel" aria-hidden="true" />
      </div>
    </section>
  );
}

function OrdersBoard({
  branchId,
  statuses,
  columnOrder,
  typeId,
  dateFrom,
  dateTo,
  searchQuery,
  onOpenOrder,
  onOpenColumns,
  requestWithMeta,
  statusesReady,
}) {
  const boardRef = React.useRef(null);
  const pendingFlipRef = React.useRef(null);
  const columnsRef = React.useRef({});
  const loadGeneration = React.useRef(0);
  const columnsPromiseRef = React.useRef(null);
  const columnsPromiseKeyRef = React.useRef('');
  const loadingMoreIds = React.useRef(new Set());
  const stopDragRef = React.useRef(() => {});
  const ignoreOpenRef = React.useRef(false);
  const [columns, setColumns] = React.useState({});
  const [boardError, setBoardError] = React.useState(null);
  const [dragVisual, setDragVisual] = React.useState(null);
  const [dropStatusId, setDropStatusId] = React.useState(null);
  const [cancelDrop, setCancelDrop] = React.useState(null);

  const sections = React.useMemo(() => buildCardSections(columnOrder), [columnOrder]);
  const statusKey = statuses.map((status) => status.id).join(',');
  columnsRef.current = columns;

  const filters = React.useMemo(() => ({
    typeId,
    dateFrom,
    dateTo,
    searchQuery,
  }), [typeId, dateFrom, dateTo, searchQuery]);

  React.useLayoutEffect(() => {
    const before = pendingFlipRef.current;
    if (!before) return;
    pendingFlipRef.current = null;
    animateCardMoves(boardRef.current, before);
  }, [columns]);

  React.useEffect(() => () => stopDragRef.current(), []);

  React.useEffect(() => {
    const board = boardRef.current;
    if (!board) return undefined;
    const onWheel = (event) => {
      if (board.classList.contains('is-dragging')) return;
      const absX = Math.abs(event.deltaX);
      const absY = Math.abs(event.deltaY);
      const horizontal = event.shiftKey || absX > absY;
      if (!horizontal) return;
      const raw = absX > absY ? event.deltaX : event.deltaY;
      if (!raw || board.scrollWidth <= board.clientWidth) return;
      const line = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? board.clientWidth : 1;
      board.scrollLeft += raw * line;
      event.preventDefault();
    };
    board.addEventListener('wheel', onWheel, { passive: false, capture: true });
    return () => board.removeEventListener('wheel', onWheel, { capture: true });
  }, []);

  React.useEffect(() => {
    if (!statusesReady || statuses.length === 0) return undefined;
    const generation = loadGeneration.current + 1;
    loadGeneration.current = generation;
    const next = {};
    statuses.forEach((status) => {
      next[status.id] = emptyColumn(true);
    });
    setColumns(next);
    setBoardError(null);

    const statusIds = statuses.map((status) => status.id);
    const requestKey = [
      branchId,
      statusKey,
      filters.typeId,
      filters.dateFrom || '',
      filters.dateTo || '',
      filters.searchQuery || '',
    ].join('|');
    if (columnsPromiseKeyRef.current !== requestKey) {
      columnsPromiseKeyRef.current = requestKey;
      columnsPromiseRef.current = fetchBoardColumns(
        requestWithMeta,
        branchId,
        statusIds,
        filters
      );
    }
    columnsPromiseRef.current.then((result) => {
      if (generation !== loadGeneration.current) return;
      setColumns((prev) => {
        const loaded = { ...prev };
        statusIds.forEach((statusId) => {
          const column = result.columns[statusId] ?? result.columns[String(statusId)];
          loaded[statusId] = {
            orders: Array.isArray(column?.orders) ? column.orders : [],
            page: 1,
            total: column?.pagination?.total ?? (result.ok ? 0 : null),
            totalPages: column?.pagination?.totalPages ?? (result.ok ? 0 : 1),
            loading: false,
            loadingMore: false,
            error: result.ok ? null : result.error,
          };
        });
        return loaded;
      });
    }).catch(() => {
      if (generation !== loadGeneration.current) return;
      setColumns((prev) => {
        const failed = { ...prev };
        statusIds.forEach((statusId) => {
          failed[statusId] = { ...emptyColumn(false), error: 'Ошибка загрузки' };
        });
        return failed;
      });
    });
    return undefined;
  }, [branchId, statusKey, filters, requestWithMeta, statuses, statusesReady]);

  const loadMore = React.useCallback((statusId) => {
    const generation = loadGeneration.current;
    const current = columnsRef.current[statusId];
    if (!current || current.loading || current.loadingMore || current.page >= current.totalPages) return;
    if (loadingMoreIds.current.has(statusId)) return;
    loadingMoreIds.current.add(statusId);
    setColumns((prev) => {
      const column = prev[statusId];
      if (!column || column.loadingMore) return prev;
      return { ...prev, [statusId]: { ...column, loadingMore: true } };
    });
    const finish = () => loadingMoreIds.current.delete(statusId);
    fetchBoardPage(requestWithMeta, branchId, statusId, current.page + 1, filters)
      .then((result) => {
        finish();
        if (generation !== loadGeneration.current) return;
        setColumns((prev) => {
          const column = prev[statusId];
          if (!column) return prev;
          if (!result.ok) {
            return {
              ...prev,
              [statusId]: { ...column, loadingMore: false, error: result.error },
            };
          }
          const seen = new Set(column.orders.map((order) => order.id));
          const added = result.orders.filter((order) => !seen.has(order.id));
          return {
            ...prev,
            [statusId]: {
              ...column,
              orders: [...column.orders, ...added],
              page: column.page + 1,
              total: result.total ?? column.total,
              totalPages: result.totalPages,
              loadingMore: false,
              error: null,
            },
          };
        });
      })
      .catch(() => {
        finish();
        if (generation !== loadGeneration.current) return;
        setColumns((prev) => {
          const column = prev[statusId];
          if (!column) return prev;
          return {
            ...prev,
            [statusId]: { ...column, loadingMore: false, error: 'Ошибка загрузки' },
          };
        });
      });
  }, [branchId, filters, requestWithMeta]);

  const commitMove = React.useCallback(async (order, fromStatusId, toStatusId) => {
    pendingFlipRef.current = {
      rects: captureCardRects(boardRef.current),
      movedId: order.id,
    };
    setBoardError(null);
    setColumns((prev) => moveBetween(prev, order, fromStatusId, toStatusId));
    try {
      const res = await requestWithMeta(
        `/operator/orders/${encodeURIComponent(order.id)}/status`,
        'PUT',
        { statusId: toStatusId }
      );
      if (res?.ok) return;
      pendingFlipRef.current = {
        rects: captureCardRects(boardRef.current),
        movedId: order.id,
      };
      setColumns((prev) => moveBetween(prev, order, toStatusId, fromStatusId));
      setBoardError(res?.data?.error || res?.data?.message || 'Не удалось изменить статус');
    } catch (_) {
      pendingFlipRef.current = {
        rects: captureCardRects(boardRef.current),
        movedId: order.id,
      };
      setColumns((prev) => moveBetween(prev, order, toStatusId, fromStatusId));
      setBoardError('Не удалось изменить статус');
    }
  }, [requestWithMeta]);

  const beginDrag = React.useCallback((event, order, fromStatusId) => {
    if (event.button != null && event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const card = event.currentTarget.closest('.orders-board-card');
    const rect = card?.getBoundingClientRect();
    if (!rect) return;

    const drag = {
      order,
      fromStatusId,
      originX: event.clientX,
      originY: event.clientY,
      clientX: event.clientX,
      clientY: event.clientY,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      width: rect.width,
      moved: false,
      frame: 0,
    };
    boardRef.current?.classList.add('is-dragging');
    setDragVisual({
      order,
      fromStatusId,
      x: event.clientX,
      y: event.clientY,
      offsetX: drag.offsetX,
      offsetY: drag.offsetY,
      width: drag.width,
    });

    const statusUnderPointer = (clientX, clientY) => {
      const node = document.elementFromPoint(clientX, clientY);
      const column = node?.closest?.('[data-status-id]');
      if (!column) return null;
      const id = Number(column.getAttribute('data-status-id'));
      return Number.isFinite(id) ? id : null;
    };

    const tick = () => {
      if (drag.stopped) return;
      const board = boardRef.current;
      if (!board) return;
      const bounds = board.getBoundingClientRect();
      let delta = 0;
      if (drag.clientX < bounds.left + EDGE_PX) {
        const closeness = (EDGE_PX - (drag.clientX - bounds.left)) / EDGE_PX;
        delta = -MAX_SCROLL_SPEED * Math.min(1, Math.max(0, closeness));
      } else if (drag.clientX > bounds.right - EDGE_PX) {
        const closeness = (EDGE_PX - (bounds.right - drag.clientX)) / EDGE_PX;
        delta = MAX_SCROLL_SPEED * Math.min(1, Math.max(0, closeness));
      }
      if (delta) board.scrollLeft += delta;
      drag.frame = window.requestAnimationFrame(tick);
    };
    drag.frame = window.requestAnimationFrame(tick);

    const move = (pointerEvent) => {
      if (Math.hypot(pointerEvent.clientX - drag.originX, pointerEvent.clientY - drag.originY) > 4) {
        drag.moved = true;
      }
      drag.clientX = pointerEvent.clientX;
      drag.clientY = pointerEvent.clientY;
      const nextDrop = statusUnderPointer(pointerEvent.clientX, pointerEvent.clientY);
      setDragVisual((prev) => (prev ? {
        ...prev,
        x: pointerEvent.clientX,
        y: pointerEvent.clientY,
      } : prev));
      setDropStatusId((prev) => (prev === nextDrop ? prev : nextDrop));
    };

    const end = (pointerEvent, cancelled) => {
      if (drag.stopped) return;
      drag.stopped = true;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      window.cancelAnimationFrame(drag.frame);
      boardRef.current?.classList.remove('is-dragging');
      stopDragRef.current = () => {};
      setDragVisual(null);
      setDropStatusId(null);
      if (drag.moved) {
        ignoreOpenRef.current = true;
        window.setTimeout(() => {
          ignoreOpenRef.current = false;
        }, 400);
      }
      if (cancelled) return;
      const toStatusId = statusUnderPointer(pointerEvent.clientX, pointerEvent.clientY);
      if (toStatusId == null || toStatusId === fromStatusId) return;
      if (toStatusId === OrderStatusTypes.CANCELED) {
        setCancelDrop({ order, fromStatusId, toStatusId });
        return;
      }
      commitMove(order, fromStatusId, toStatusId);
    };
    const onUp = (pointerEvent) => end(pointerEvent, false);
    const onCancel = (pointerEvent) => end(pointerEvent, true);
    const stop = () => end({ clientX: drag.clientX, clientY: drag.clientY }, true);
    stopDragRef.current = stop;
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
  }, [commitMove]);

  const ghost = dragVisual && typeof document !== 'undefined'
    ? createPortal(
      <OrderCard
        className="orders-board-ghost"
        style={{
          left: dragVisual.x - dragVisual.offsetX,
          top: dragVisual.y - dragVisual.offsetY,
          width: dragVisual.width,
        }}
        order={dragVisual.order}
        sections={sections}
        showHandle={isSalesOrder(dragVisual.order)}
        onOpen={() => {}}
        onHandlePointerDown={() => {}}
      />,
      document.body
    )
    : null;

  return (
    <>
      {boardError ? <div className="orders-board-error" role="alert">{boardError}</div> : null}
      <div className={`orders-board${dragVisual ? ' is-dragging' : ''}`} ref={boardRef}>
        {statuses.map((status) => (
          <BoardColumn
            key={status.id}
            status={status}
            column={columns[status.id] || emptyColumn(true)}
            sections={sections}
            isDrop={dropStatusId === status.id && dropStatusId !== dragVisual?.fromStatusId}
            draggingOrderId={dragVisual?.order?.id ?? null}
            onOpenOrder={(orderId) => {
              if (ignoreOpenRef.current) return;
              onOpenOrder(orderId);
            }}
            onOpenColumns={onOpenColumns}
            onHandlePointerDown={beginDrag}
            onLoadMore={loadMore}
          />
        ))}
      </div>
      {ghost}
      <CancelOrderConfirmModal
        isOpen={Boolean(cancelDrop)}
        orderLabel={cancelDrop ? `Заказ № ${cancelDrop.order.id}` : ''}
        onClose={() => setCancelDrop(null)}
        onConfirm={() => {
          const pending = cancelDrop;
          setCancelDrop(null);
          if (!pending) return;
          commitMove(pending.order, pending.fromStatusId, pending.toStatusId);
        }}
      />
    </>
  );
}

export default OrdersBoard;
